import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes, faExternalLinkAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../App';
import '../styles/AnnouncementBanner.css';

const AnnouncementBanner = ({ style, auth }) => {
  const [bannerData, setBannerData] = useState(null);
  const [allLevels, setAllLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [generalBanner, setGeneralBanner] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/banner`);
        if (response.data.success && Array.isArray(response.data.data)) {
          setAllLevels(response.data.data);
          
          // تخزين البانر العام إذا وجد
          if (response.data.banner) {
            setGeneralBanner(response.data.banner);
          }
          
          // التحقق من وجود أي بانر في أي مستوى
          const hasAnyBanner = response.data.data.some(level => 
            level.banners && Array.isArray(level.banners) && level.banners.length > 0
          );
          
          // إذا لم يوجد أي بانر ولا يوجد بانر عام، لا تعرض شيئاً
          if (!hasAnyBanner && !response.data.banner) {
            setBannerData(null);
            setIsLoading(false);
            return;
          }
          
          // تحديد المستوى المناسب
          let userLevelId = auth && auth.level ? Number(auth.level) : null;
          let targetLevel = null;
          
          if (userLevelId) {
            // المستخدم مسجل دخول - البحث عن مستواه
            targetLevel = response.data.data.find(l => Number(l.level_id) === userLevelId);
          }
          
          // إذا لم يجد المستوى أو المستخدم غير مسجل دخول
          if (!targetLevel && response.data.data.length > 0) {
            // البحث عن أول مستوى يحتوي على banners
            targetLevel = response.data.data.find(l => 
              l.banners && Array.isArray(l.banners) && l.banners.length > 0
            );
            
            // إذا لم يجد أي مستوى يحتوي على banners، استخدم الأول
            if (!targetLevel) {
              targetLevel = response.data.data[0];
            }
          }
          
          if (targetLevel) {
            setSelectedLevelId(Number(targetLevel.level_id));
            
            if (targetLevel.banners && Array.isArray(targetLevel.banners) && targetLevel.banners.length > 0) {
              // إذا كان المستوى يحتوي على banners خاصة به
              setBannerData({ 
                ...targetLevel.banners[0], 
                level_name: targetLevel.level_name 
              });
            } else if (response.data.banner) {
              // البحث عن البانر العام
              setBannerData({ 
                ...response.data.banner, 
                level_name: targetLevel.level_name,
                title: response.data.banner.name || response.data.banner.title || 'إعلان مهم'
              });
            } else {
              // لا يوجد أي بانر
              setBannerData(null);
            }
          } else {
            setBannerData(null);
          }
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBannerData(null);
      } finally {
        setIsLoading(false);
      }
    };

    // جلب البيانات مرة واحدة فقط عند تحميل المكون
    fetchBanner();
  }, []); // يعمل مرة واحدة فقط عند التحميل

  // Handle toggle for guest (not logged in)
  const handleLevelToggle = useCallback((levelId) => {
    // تجنب التحديث إذا كان نفس المستوى محدد بالفعل
    if (Number(levelId) === selectedLevelId) {
      return;
    }
    
    setSelectedLevelId(levelId);
    const level = allLevels.find(l => Number(l.level_id) === levelId);
    
    if (level && Array.isArray(level.banners) && level.banners.length > 0) {
      // إذا كان المستوى يحتوي على banners
      setBannerData({ ...level.banners[0], level_name: level.level_name });
    } else if (level && generalBanner) {
      // إذا كان المستوى لا يحتوي على banners، استخدم البانر العام
      setBannerData({ 
        ...generalBanner, 
        level_name: level.level_name,
        title: generalBanner.name || generalBanner.title || 'إعلان مهم'
      });
    } else {
      // لا يوجد أي بانر
      setBannerData(null);
    }
  }, [selectedLevelId, allLevels, generalBanner]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleClick = useCallback(() => {
    if (bannerData?.link_url) {
      window.open(bannerData.link_url, '_blank');
    }
  }, [bannerData?.link_url]);

  if (isLoading || !bannerData || !isVisible) {
    return null;
  }

  // Show toggles if not logged in or no level
  const showToggles = !(auth && auth.level);

  return (
    <div className={`announcement-banner ${style ? 'dark-mode' : ''}`}>
      <div className="banner-content">
        <div className="banner-icon">
          <FontAwesomeIcon icon={faBell} />
        </div>
        <div className="banner-text">
          <h3>{bannerData.title}</h3>
          <p>{bannerData.description}</p>
          {bannerData.level_name && (
            <div style={{ fontSize: '1rem', opacity: 0.7, marginTop: 4 }}>{bannerData.level_name}</div>
          )}
        </div>
        <div className="banner-actions">
          {bannerData.link_url && (
            <button 
              className="banner-link-btn"
              onClick={handleClick}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <span>شوف التفاصيل</span>
            </button>
          )}
          <button 
            className="banner-close-btn"
            onClick={handleClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {showToggles && allLevels.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            {allLevels.map(level => (
              <button
                key={level.level_id}
                onClick={() => handleLevelToggle(Number(level.level_id))}
                style={{
                  background: Number(level.level_id) === selectedLevelId ? '#fff' : 'rgba(255,255,255,0.15)',
                  color: Number(level.level_id) === selectedLevelId ? '#00b4b4' : '#fff',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: 16,
                  padding: '6px 14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  marginRight: 4
                }}
              >
                {level.level_name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="banner-decoration">
        <div className="decoration-circle circle-1"></div>
        <div className="decoration-circle circle-2"></div>
        <div className="decoration-circle circle-3"></div>
      </div>
    </div>
  );
};

export default AnnouncementBanner; 